import {
  API_DOCS_DOWNLOAD,
  API_DOCS_MULTI_DOWNLOAD,
  API_DOCUMENT_CREATE,
  API_DOCUMENT_DELETE,
  API_DOCUMENT_GET,
  API_DOCUMENT_LIST_ALL,
  API_DOCUMENT_UPDATE,
  API_GUIDELINE_CONDITION,
  API_RATESHEET,
  API_RATESHEET_UPDATE,
  API_RESOURCE_COMPANY_APPRAISAL,
  API_RESOURCE_COMPANY_APPRAISAL_BY_ID,
  API_RESOURCE_LOAN_PROGRAM_BY_PROGRAM_ID,
  API_RESOURCE_PROGRAM_RESTRICTION,
  API_RESOURCE_RBT_DEF,
  API_TEMPLATE_PROGRAMS,
  baseUrl,
} from 'config'
import { store } from 'reducers'
import Api from 'services/api'
import { getFileExt, getFileName } from 'utils'

export const downloadS3Documents = (key: string) => {
  return Api.post(API_DOCS_DOWNLOAD, { key })
}

export const listAllDocuments = () => {
  return Api.get(API_DOCUMENT_LIST_ALL)
}

export const getTemplatePrograms = () => {
  return Api.get(API_TEMPLATE_PROGRAMS)
}

export const getDocument = (id: number) => {
  return Api.get(API_DOCUMENT_GET, {}, { id })
}

export const fileJsonConvert = (data: Record<string, string | string[] | File>) => {
  const files: File[] = []
  Object.keys(data).forEach((key) => {
    if (data[key] && (data[key] as any).size) {
      files.push(data[key] as any)
      delete data[key]
    }
  })
  return { data, files }
}

export const addNewDocument = (data: Record<string, string | string[] | File>) => {
  const json = fileJsonConvert(data)
  return Api.uploadFiles(API_DOCUMENT_CREATE, json.data, {}, json.files)
}

export const updateDocument = (id: number, data: Record<string, string | string[] | File>) => {
  const json = fileJsonConvert(data)
  return Api.uploadFiles(API_DOCUMENT_UPDATE, json.data, { id }, json.files)
}

export const deleteDocument = (id: number) => {
  return Api.delete(API_DOCUMENT_DELETE, {}, { id })
}

export const updateDocumentOrders = (data: Record<string, any>[]) => {
  return Api.put(API_DOCUMENT_CREATE, data)
}

// Extensions a browser renders in a tab of its own. Anything else downloads instead, because
// window.open() on a file the browser cannot display leaves the tab it just opened blank forever
// while the download happens behind it, which reads as a broken link. Office documents, archives and
// saved mail (.eml, .msg, and the .rpmsg Microsoft 365 Message Encryption attaches) all land here.
const PREVIEWABLE_EXTS = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'bmp',
  'avif',
  'txt',
  'htm',
  'html',
  'json',
  'xml',
  'csv',
  'mp3',
  'mp4',
  'wav',
  'mov',
]

// A key with no extension keeps the old behaviour: there is nothing to decide on, and some stored
// keys (inline email images, for one) genuinely have none.
const isPreviewableInBrowser = (fileKey: string) => {
  const ext = getFileExt(fileKey)
  return !ext || PREVIEWABLE_EXTS.includes(ext)
}

// Fetches the signed url in a hidden frame so the browser downloads it without opening a tab.
const downloadInBackground = (url: string) => {
  const frame = document.createElement('iframe')
  frame.style.display = 'none'
  frame.src = url
  document.body.appendChild(frame)
  setTimeout(() => frame.remove(), 60 * 1000)
}

export const openS3Document = async (fileKey: string, isNewWindow = false) => {
  const res = await downloadS3Documents(fileKey)
  if (res.content) {
    downloadFile(getFileName(fileKey), new Blob([res.content]))
    return
  }
  if (!isPreviewableInBrowser(fileKey)) {
    downloadInBackground(res.url)
    return
  }
  if (!isNewWindow) {
    var windowReference: any = window.open()
    windowReference.location = res.url
  } else {
    window.open(res.url, '_blank', 'resizable=yes')
  }
}

export const openAsyncS3Document = async (fileKey: string, popupWindow: Window) => {
  const res = await downloadS3Documents(fileKey)
  if (res.content) {
    downloadFile(getFileName(fileKey), new Blob([res.content]))
    return
  }
  popupWindow.location.href = res.url
}

export const downloadS3Document = async (fileKey: string, fileName: string) => {
  const res: Blob = await Api.post(
    API_DOCS_DOWNLOAD,
    { key: fileKey, fileName },
    {},
    {
      responseType: 'blob',
    },
  )

  if (!getFileExt(fileName)) {
    const ext = getFileExt(fileKey)
    if (ext) {
      if (fileName.toLowerCase().endsWith(ext)) fileName = fileName.substring(0, fileName.length - ext.length)
      fileName += `.${ext}`
    }
  }

  downloadFile(fileName, res)
}

export const downloadFile = (fileName: string, contentBlob: Blob) => {
  const aElement = document.createElement('a')
  aElement.setAttribute('download', fileName)
  const href = URL.createObjectURL(contentBlob)
  aElement.href = href
  aElement.setAttribute('target', '_blank')
  aElement.click()
  URL.revokeObjectURL(href)
}

export const addMultiDocuments = (
  loanNumber: number,
  fileName: string,
  keys: string[],
  entity: string = '',
): Promise<{ docId: string }> => {
  return Api.post(API_DOCS_MULTI_DOWNLOAD, { loanNumber, fileName, keys, entity })
}

export const openS3Documents = async (
  loanNumber: number,
  fileName: string,
  keys: string[],
  entity: string = '',
  format: 'zip' | 'pdf' = 'zip',
) => {
  const state = store.getState()
  const {
    auth: { token },
  } = state

  const { docId } = await addMultiDocuments(loanNumber, fileName, keys, entity)
  const url = `${baseUrl}${API_DOCS_MULTI_DOWNLOAD}/${docId}?token=${token}&format=${format}`
  const windowReference: any = window.open()
  windowReference.document.title = 'Downloading Documents...'
  windowReference.location = url
}

export const listAllRatesheet = (filters: Record<string, any>) => {
  return Api.get(API_RATESHEET, filters)
}

export const addRatesheet = (fileKey: string) => {
  return Api.post(API_RATESHEET, { fileKey })
}

export const updateRatesheet = (id: number, date: string) => {
  return Api.put(API_RATESHEET_UPDATE, { date }, { id })
}

export const deleteRatesheet = (id: number) => {
  return Api.delete(API_RATESHEET_UPDATE, {}, { id })
}

export const getResourceProgramRestrictions = (programs: string[]) => {
  return Api.get(API_RESOURCE_PROGRAM_RESTRICTION, { programs: programs.join(',') })
}

export const getLoanProgramsByProgramID = (programID: number, data: Record<string, any>) => {
  return Api.post(API_RESOURCE_LOAN_PROGRAM_BY_PROGRAM_ID, data, { programID })
}

export const getRBTDef = (programID: number) => {
  return Api.get(API_RESOURCE_RBT_DEF, { programID })
}

export const getGuidelineConditions = (id: number) => {
  return Api.get(API_GUIDELINE_CONDITION, {}, { id })
}

export const postGuidelineCondition = (id: number, data: Record<string, any>) => {
  return Api.post(API_GUIDELINE_CONDITION, data, { id })
}

export const deleteGuidelineCondition = (id: number) => {
  return Api.delete(API_GUIDELINE_CONDITION, {}, { id })
}

// resource part

export const addNewCompanyAppraisal = (data: Record<string, any>) => {
  return Api.post(API_RESOURCE_COMPANY_APPRAISAL, data)
}

export const getAllCompanyAppraisal = () => {
  return Api.get(API_RESOURCE_COMPANY_APPRAISAL)
}

export const updateCompanyAppraisal = (data: Record<string, any>, id: number) => {
  return Api.put(API_RESOURCE_COMPANY_APPRAISAL_BY_ID, data, { id })
}

export const deleteCompanyAppraisal = (id: number) => {
  return Api.delete(API_RESOURCE_COMPANY_APPRAISAL_BY_ID, {}, { id })
}

export const sortCompanyAppraisal = (data: number[]) => {
  return Api.post(API_RESOURCE_COMPANY_APPRAISAL_BY_ID, data, { id: 'sort' })
}
